import { useId, type ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, CircleAlert, Info, Inbox, RotateCw, X, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from './Button';
import { errorMessage } from '@/lib/api/query';

// ---------------------------------------------------------------------------------------------
// EmptyState / ErrorState
// ---------------------------------------------------------------------------------------------

export interface EmptyStateProps {
  icon?: LucideIcon;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  secondaryAction?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Dashed outline for empty tables and drop zones. */
  bordered?: boolean;
  className?: string;
  tone?: 'brand' | 'danger' | 'warning';
}

const glowTone = {
  brand: { halo: 'bg-sapphire-500/15', ring: 'ring-sapphire-200 dark:ring-sapphire-700', icon: 'text-sapphire-600 dark:text-sapphire-300', tile: 'bg-sapphire-50 dark:bg-raised' },
  danger: { halo: 'bg-danger-500/15', ring: 'ring-danger-100 dark:ring-danger-700/50', icon: 'text-danger-600 dark:text-danger-500', tile: 'bg-danger-50 dark:bg-raised' },
  warning: { halo: 'bg-warning-500/15', ring: 'ring-warning-100 dark:ring-warning-700/50', icon: 'text-warning-600 dark:text-warning-500', tile: 'bg-warning-50 dark:bg-raised' },
} as const;

/** A designed empty state: layered glow tile, a clear sentence, and the next action. Never a bare "No data". */
export function EmptyState({ icon: Icon = Inbox, title, description, action, secondaryAction, size = 'md', bordered, className, tone = 'brand' }: EmptyStateProps) {
  const t = glowTone[tone];
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center overflow-hidden text-center',
        size === 'sm' ? 'px-4 py-8' : size === 'md' ? 'px-6 py-14' : 'px-8 py-24',
        bordered && 'rounded-card border border-dashed border-line-strong',
        className,
      )}
    >
      <div className="relative mb-5">
        <div className={cn('absolute -inset-6 rounded-full blur-2xl', t.halo)} aria-hidden="true" />
        <div className="absolute -inset-3 rounded-[1.75rem] border border-line opacity-70" aria-hidden="true" />
        <div className={cn('relative grid place-items-center rounded-2xl ring-1', t.tile, t.ring, size === 'sm' ? 'h-12 w-12' : 'h-16 w-16')}>
          <Icon className={cn(t.icon, size === 'sm' ? 'h-5 w-5' : 'h-7 w-7')} strokeWidth={1.6} aria-hidden="true" />
        </div>
      </div>
      <h3 className="font-display text-lg font-semibold text-ink">{title}</h3>
      {description && <p className="mt-1.5 max-w-sm text-sm text-ink-muted">{description}</p>}
      {(action || secondaryAction) && (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          {action}
          {secondaryAction}
        </div>
      )}
    </div>
  );
}

export interface ErrorStateProps {
  title?: ReactNode;
  /** An Error (ApiError messages are shown as-is) or a plain description. */
  error?: unknown;
  description?: ReactNode;
  onRetry?: () => void;
  retrying?: boolean;
  size?: EmptyStateProps['size'];
  className?: string;
}

/** Inline, actionable error: says what failed and offers Try again. */
export function ErrorState({ title = 'We could not load this', error, description, onRetry, retrying, size = 'md', className }: ErrorStateProps) {
  return (
    <EmptyState
      icon={CircleAlert}
      tone="danger"
      size={size}
      className={className}
      title={title}
      description={description ?? (error ? errorMessage(error) : 'Something went wrong on our side. Please try again.')}
      action={
        onRetry && (
          <Button variant="secondary" size="sm" onClick={onRetry} loading={retrying} leftIcon={<RotateCw className="h-4 w-4" aria-hidden="true" />}>
            Try again
          </Button>
        )
      }
    />
  );
}

// ---------------------------------------------------------------------------------------------
// Alert
// ---------------------------------------------------------------------------------------------

export type AlertTone = 'info' | 'success' | 'warning' | 'danger' | 'brand';

const alertTones: Record<AlertTone, { box: string; icon: LucideIcon; iconClass: string }> = {
  info: { box: 'border-ice-200 bg-ice-50 text-ice-700 dark:border-ice-400/25 dark:bg-ice-500/10 dark:text-ice-300', icon: Info, iconClass: 'text-ice-500' },
  brand: { box: 'border-sapphire-200 bg-sapphire-50 text-sapphire-900 dark:border-sapphire-500/30 dark:bg-sapphire-700/15 dark:text-sapphire-100', icon: Info, iconClass: 'text-sapphire-600 dark:text-sapphire-300' },
  success: { box: 'border-success-100 bg-success-50 text-success-700 dark:border-success-500/25 dark:bg-success-500/10 dark:text-success-500', icon: CheckCircle2, iconClass: 'text-success-600' },
  warning: { box: 'border-warning-100 bg-warning-50 text-warning-700 dark:border-warning-500/25 dark:bg-warning-500/10 dark:text-warning-500', icon: AlertTriangle, iconClass: 'text-warning-600' },
  danger: { box: 'border-danger-100 bg-danger-50 text-danger-700 dark:border-danger-500/25 dark:bg-danger-500/10 dark:text-danger-500', icon: CircleAlert, iconClass: 'text-danger-600' },
};

export interface AlertProps {
  tone?: AlertTone;
  title?: ReactNode;
  children?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
  onDismiss?: () => void;
  /** Compact single-line variant for forms and table toolbars. */
  dense?: boolean;
  className?: string;
}

export function Alert({ tone = 'info', title, children, icon, action, onDismiss, dense, className }: AlertProps) {
  const t = alertTones[tone];
  const Icon = t.icon;
  const urgent = tone === 'danger' || tone === 'warning';
  return (
    <div role={urgent ? 'alert' : 'status'} className={cn('flex items-start gap-3 rounded-xl border', dense ? 'px-3 py-2 text-sm' : 'p-4 text-sm', t.box, className)}>
      {icon ? (
        <span className={cn('mt-0.5 shrink-0', t.iconClass)}>{icon}</span>
      ) : (
        <Icon className={cn('mt-0.5 h-4.5 w-4.5 shrink-0', t.iconClass)} style={{ width: '1.125rem', height: '1.125rem' }} aria-hidden="true" />
      )}
      <div className="min-w-0 flex-1">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={cn(title && 'mt-0.5', 'text-ink/90')}>{children}</div>}
        {action && <div className="mt-2">{action}</div>}
      </div>
      {onDismiss && (
        <button type="button" onClick={onDismiss} aria-label="Dismiss" className="-m-1 rounded-md p-1 opacity-70 transition hover:opacity-100">
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

/** Dense Alert for use inside forms. */
export function InlineAlert(props: Omit<AlertProps, 'dense'>) {
  return <Alert dense {...props} />;
}

// ---------------------------------------------------------------------------------------------
// ProgressBar
// ---------------------------------------------------------------------------------------------

export interface ProgressBarProps {
  /** 0..max. Omit for an indeterminate bar. */
  value?: number;
  max?: number;
  label?: ReactNode;
  showValue?: boolean;
  tone?: 'brand' | 'success' | 'warning' | 'danger';
  size?: 'sm' | 'md';
  className?: string;
}

const barTone = {
  brand: 'bg-sapphire-600 dark:bg-sapphire-400',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  danger: 'bg-danger-500',
} as const;

export function ProgressBar({ value, max = 100, label, showValue, tone = 'brand', size = 'md', className }: ProgressBarProps) {
  const id = useId();
  const determinate = value !== undefined;
  const pct = determinate ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div className={className}>
      {(label || showValue) && (
        <div className="mb-1.5 flex items-center justify-between text-xs">
          <span id={id} className="font-medium text-ink">
            {label}
          </span>
          {showValue && determinate && <span className="tabular text-ink-muted">{Math.round(pct)}%</span>}
        </div>
      )}
      <div
        role="progressbar"
        aria-labelledby={label ? id : undefined}
        aria-label={label ? undefined : 'Progress'}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={determinate ? value : undefined}
        className={cn('overflow-hidden rounded-full bg-sunken ring-1 ring-inset ring-line', size === 'sm' ? 'h-1.5' : 'h-2.5')}
      >
        <div
          className={cn('h-full rounded-full transition-[width] duration-500 ease-out motion-reduce:transition-none', barTone[tone], !determinate && 'w-1/3 animate-pulse')}
          style={determinate ? { width: `${pct}%` } : undefined}
        />
      </div>
    </div>
  );
}
